/**
 * The image strip on a tournament card and at the top of a tournament page.
 *
 * A director who uploaded a banner gets their banner. One who did not gets art
 * the app draws from the tournament's own name and id, so a row of cards is one
 * set rather than a mix of photographs and blank space (spec
 * `docs/superpowers/specs/2026-08-16-tournament-fallback-banner-design.md`).
 *
 * Every ground is a stop on the brand ramp and every ink is `--brand-fg`, which
 * `readableForeground` in `@alpha/shared` already computes to clear WCAG AA
 * against `brand-600`. That is the whole readability argument: reuse the
 * guarantee the branding feature tested rather than invent a second one. It is
 * also why there is not a hex literal in this file — a hardcoded color would
 * quietly opt the banner out of the admin's brand color.
 */
import { useId } from "react";
import { bannerVariant, tournamentInitials, type BannerVariant } from "../lib/tournamentArt";
import { cx } from "./ui";

type BannerSize = "card" | "hero";

/**
 * The generated art is half the height of an uploaded photo on the tournament
 * page: 320px of abstract panel is more than two letters have earned, and a
 * director who uploads a real image should visibly get more for it. The card
 * uses one height for both — a shorter generated banner there would put back,
 * at the top of the card, the very mismatch this removes.
 */
const SIZES = {
  card: { img: "h-32", art: "h-32", extra: "", w: 320, h: 128 },
  hero: {
    img: "h-48 sm:h-64 lg:h-80",
    art: "h-24 sm:h-32 lg:h-40",
    extra: "animate-rise rounded-2xl",
    w: 960,
    h: 160,
  },
} as const;

export function TournamentBanner({
  name,
  seed,
  src,
  size,
}: {
  name: string;
  seed: string;
  /** The uploaded banner, when the director set one. */
  src?: string | null;
  size: BannerSize;
}) {
  const box = SIZES[size];
  if (src)
    return <img src={src} alt="" className={cx("w-full object-cover", box.img, box.extra)} />;
  return <GeneratedBanner name={name} seed={seed} size={size} />;
}

/** A right triangle filling one corner, `depth` of the box wide at its base. */
function wedgePath({ corner, depth }: BannerVariant, w: number, h: number): string {
  const dx = w * depth;
  switch (corner) {
    case "tl":
      return `M0 0 L${dx} 0 L0 ${h}Z`;
    case "tr":
      return `M${w} 0 L${w - dx} 0 L${w} ${h}Z`;
    case "bl":
      return `M0 ${h} L${dx} ${h} L0 0Z`;
    case "br":
      return `M${w} ${h} L${w - dx} ${h} L${w} 0Z`;
  }
}

const OPPOSITE = { tl: "br", tr: "bl", bl: "tr", br: "tl" } as const;

function GeneratedBanner({ name, seed, size }: { name: string; seed: string; size: BannerSize }) {
  const { w, h, art, extra } = SIZES[size];
  const variant = bannerVariant(seed);
  const initials = tournamentInitials(name);
  // Unique per document, which is what a grid of a dozen banners needs.
  // `useId` writes colons, legal in a URL fragment but not in a CSS selector,
  // so they come out here rather than surprising someone later.
  const dotsId = `banner-dots-${useId().replace(/:/g, "")}`;

  // The mark balances the wedge from the far corner.
  const markCorner = OPPOSITE[variant.corner];
  const inset = h * 0.26;
  const markX = markCorner === "tl" || markCorner === "bl" ? inset : w - inset;
  const markY = markCorner === "tl" || markCorner === "tr" ? inset : h - inset;
  const markR = h * 0.1;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      // Crop rather than distort: a squashed diamond reads as a mistake, a
      // trimmed edge does not. Cropping is symmetric about the center, which is
      // why the monogram is centered — the middle of the box is the only region
      // that survives both a phone's 3.6:1 and a desktop column's 7.2:1.
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
      className={cx("w-full", art, extra)}
    >
      <defs>
        <pattern
          id={dotsId}
          width="16"
          height="16"
          patternUnits="userSpaceOnUse"
          patternTransform={`translate(${variant.phase} ${variant.phase})`}
        >
          <circle cx="2" cy="2" r="1.6" className="fill-brandFg" opacity="0.12" />
        </pattern>
      </defs>
      <rect width={w} height={h} className="fill-brand-600" />
      <path d={wedgePath(variant, w, h)} className="fill-brand-800" opacity="0.55" />
      <rect width={w} height={h} fill={`url(#${dotsId})`} />
      <g transform={`translate(${markX} ${markY}) rotate(45)`} opacity="0.35">
        <rect
          x={-markR}
          y={-markR}
          width={markR * 2}
          height={markR * 2}
          fill="none"
          className="stroke-brandFg"
          strokeWidth={h * 0.014}
        />
      </g>
      {initials ? (
        <text
          x={w / 2}
          y={h / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={h * 0.62}
          letterSpacing={h * -0.02}
          className="fill-brandFg font-display font-bold"
          opacity="0.95"
        >
          {initials}
        </text>
      ) : (
        <g transform={`translate(${w / 2} ${h / 2})`} opacity="0.9">
          <rect
            x={-h * 0.26}
            y={-h * 0.26}
            width={h * 0.52}
            height={h * 0.52}
            transform="rotate(45)"
            fill="none"
            className="stroke-brandFg"
            strokeWidth={h * 0.026}
          />
          <circle r={h * 0.05} className="fill-brandFg" />
        </g>
      )}
    </svg>
  );
}
